// ============================================================================
// useLoginSession — login-only, optional-first session probe.
// ----------------------------------------------------------------------------
// The login page must work for a brand-new visitor with no session, so it can
// never DEPEND on data being present. This hook taps the SAME typed API client
// + React Query cache the auth layer uses (qk.auth.me / authApi.me): if a signed
// vf_session cookie is still valid we learn the trainer's name and can warmly
// personalise the page ("Welcome back, …") and offer a one-tap resume; a 401
// (or any error / network blip) resolves to null and the page renders exactly
// as it would for someone signing in fresh.
//
// It shares the `qk.auth.me` key with AuthContext, so it stays in lockstep with
// the auth provider's live-sync — no extra request, no divergent state. The
// real sign-in still flows through useAuth().login (POST /api/auth/login); this
// probe is strictly additive presentation sugar.
// ============================================================================
import { useQuery } from "@tanstack/react-query";
import { ApiError, authApi, qk } from "../../api";
import type { Trainer } from "../../api";

export interface LoginSession {
  /** The signed-in trainer, or null when logged-out / unknown. */
  trainer: Trainer | null;
  /** First name only, for a friendly greeting. Empty when unknown. */
  firstName: string;
  /** True only once we positively know a valid session exists. */
  isReturning: boolean;
  /** Still resolving the initial probe. */
  isLoading: boolean;
}

export function useLoginSession(): LoginSession {
  const query = useQuery<Trainer | null>({
    queryKey: qk.auth.me,
    queryFn: async ({ signal }) => {
      try {
        return await authApi.me(signal);
      } catch (err) {
        // Logged-out (or offline) is a perfectly normal state on the login page.
        if (err instanceof ApiError) return null;
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const trainer = query.data ?? null;
  const firstName =
    (trainer?.display_name ?? trainer?.username ?? "").trim().split(/\s+/)[0] ??
    "";

  return {
    trainer,
    firstName,
    isReturning: !!trainer,
    isLoading: query.isLoading,
  };
}
